import React, { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { Route, useHistory } from 'react-router-dom';

import CustomPage from '../../blocks/CustomPage/CustomPage';
import NotFoundPage from '../../blocks/NotFoundPage/NotFoundPage';
import { Loader } from '../../components/Loader';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';
import { DEFAULT_DISCOVER_URL } from '../../const';
import { useSsrContext } from '../../context/SsrContext';
import { GET_PAGE_BY_URL } from '../../graphql/pages';
import { GET_VIDEO_BY_SLUG } from '../../graphql/videos';
import { SingleVideoPage } from '../../pages/SingleVideoPage';
import {
  LearnerInfo,
  Query,
  QueryGetPageByUrlArgs,
  QueryListVideosArgs,
  Tenant,
} from '../../types';

interface CustomPageLayoutProps {
  userInfo?: LearnerInfo;
  bumperUrl?: string | null;
  tenant?: Tenant | null;
}

const CustomPageLayout: React.FC<CustomPageLayoutProps> = ({ userInfo, bumperUrl, tenant }) => {
  const history = useHistory();
  const location = history?.location;
  const pathname = location?.pathname;
  const cleanPathname =
    pathname.length > 1 && pathname[pathname.length - 1] === '/' ? pathname.slice(0, -1) : pathname;
  const slug: string = cleanPathname[0] === '/' ? cleanPathname.slice(1) : cleanPathname;
  const { redirect } = useSsrContext();

  const { data: pageData, loading: pageLoading } = useQuery<
    Pick<Query, 'getPageByUrl'>,
    QueryGetPageByUrlArgs
  >(GET_PAGE_BY_URL, {
    variables: {
      input: {
        url: [cleanPathname],
      },
    },
  });

  const page = pageData?.getPageByUrl;
  const blocks = page?.blocks;

  const { data: videoData, loading: videoLoading } = useQuery<
    Pick<Query, 'listVideos'>,
    QueryListVideosArgs
  >(GET_VIDEO_BY_SLUG, {
    variables: {
      filter: {
        video_url: [slug],
      },
    },
    skip: !slug,
  });

  const video = videoData?.listVideos && videoData?.listVideos[0] ? videoData?.listVideos[0] : null;
  const hasBlocks = blocks && blocks.length > 0;

  useEffect(() => {
    const pathname = location?.pathname;
    const queryParams = new URLSearchParams(location?.search);
    const searchValue = queryParams.get('s');
    if (searchValue && !pathname.includes(DEFAULT_DISCOVER_URL)) {
      history.push(`${DEFAULT_DISCOVER_URL}/search/?s=${searchValue}`);
    }
    if (!pageLoading && !videoLoading && !video && !hasBlocks) {
      history.push('/404');
    }
  }, [history, pageLoading, videoLoading, video, hasBlocks, location]);

  if (pageLoading || videoLoading) {
    return <Loader />;
  }

  //check which was created earlier if both video and page exists
  const showVideo =
    video &&
    (!hasBlocks ||
      (hasBlocks &&
        new Date(page.created_at || 0).getTime() > new Date(video.created_at || 0).getTime()));

  if (!showVideo && redirect && page?.url && location.pathname !== page?.url) {
    redirect(page?.url || '/');
  }

  if (showVideo) {
    return (
      <Route exact path="/:slug">
        <SingleVideoPage isXApiPage={false} bumperUrl={bumperUrl} fallback={<Loader />} />
      </Route>
    );
  } else if (hasBlocks) {
    return (
      <CustomPage blocks={blocks} tenant={tenant} userInfo={userInfo} blocksLoading={pageLoading} />
    );
  } else {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }
};

export default CustomPageLayout;
